import { useGenerationProgress } from 'hooks/useGenerationProgress'
import { useCallback, useEffect } from 'react'
import { useRef } from 'react'
import { generationProgressService } from 'services/generationProgress'
import { isLoadingFinalMandatoryValue } from 'utils'
import { updateLoadingProgressDataInLocalStorage } from 'utils/localStorage'

export const useLoadingProgressUpdate = (
  id: string,
  hasPendingStatus: boolean
) => {
  const { progress, assetId } = useGenerationProgress({
    mediaItemId: id,
    isGenerating: hasPendingStatus
  })

  const progressRef = useRef<number>(progress)
  progressRef.current = progress

  const isItemGenerating =
    hasPendingStatus || isLoadingFinalMandatoryValue(progress)

  const handleBeforeUnload = useCallback(() => {
    if (progressRef.current !== null && progressRef.current < 100) {
      // read current data to preserve assetId that might have been updated by socket handlers
      updateLoadingProgressDataInLocalStorage({
        id,
        assetId,
        progress: progressRef.current
      })
    }
  }, [assetId, id])

  useEffect(() => {
    if (!isItemGenerating) return
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isItemGenerating, handleBeforeUnload])

  useEffect(() => {
    if (!isItemGenerating) return
    // If generator is not already running, restart it
    // Service will automatically restore from localStorage if available(for reloading the page or opening parallel tab)
    if (!generationProgressService.isActive(id)) {
      generationProgressService.resume(id, assetId)
    }
    // Save progress on unmount in ls
    return () => {
      handleBeforeUnload()
    }
  }, [id, isItemGenerating, handleBeforeUnload, assetId])

  return {
    isItemGenerating,
    progress
  }
}
